import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { GuidelineChunk, guidelinesApi } from "../../../api";
import { ColorsEnum } from "../../../common/theme/colors";
import * as S from "../style";

const CHUNKS_PER_PAGE = 20;

type SortField = 'chunk_index' | 'page_no' | 'status';
type SortOrder = 'asc' | 'desc';

interface ChunksListProps {
  chunks: GuidelineChunk[];
  guidelineId: string;
  onChunkUpdated?: () => void;
}

const ChunksList: React.FC<ChunksListProps> = ({ chunks, guidelineId, onChunkUpdated }) => {
  // Local state for chunks to avoid full page re-render
  const [localChunks, setLocalChunks] = useState<GuidelineChunk[]>(chunks);
  const [selectedChunk, setSelectedChunk] = useState<GuidelineChunk | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [loadingChunkIds, setLoadingChunkIds] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(CHUNKS_PER_PAGE);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('chunk_index');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Update local chunks when prop changes (e.g., on initial load or external refresh)
  useEffect(() => {
    setLocalChunks(chunks);
    // Reset visible count only when external chunks prop changes
    setVisibleCount(CHUNKS_PER_PAGE);
  }, [chunks]);

  // Helper function to get sort value for status
  const getStatusSortValue = (chunk: GuidelineChunk): number => {
    // Priority: Embedded (0) > Pending (1) > To Embed (2) > Skipped (3)
    if (chunk.embed_status) return 0; // Embedded
    if (chunk.to_embed && !chunk.embed_status) return 1; // Pending
    if (chunk.to_embed) return 2; // To Embed
    return 3; // Skipped
  };

  // Memoize sorted chunks to avoid re-sorting on every render
  const sortedChunks = useMemo(() => {
    const sorted = [...localChunks].sort((a, b) => {
      let comparison = 0;
      
      if (sortField === 'chunk_index') {
        comparison = a.chunk_index - b.chunk_index;
      } else if (sortField === 'page_no') {
        comparison = a.page_no - b.page_no;
      } else if (sortField === 'status') {
        comparison = getStatusSortValue(a) - getStatusSortValue(b);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [localChunks, sortField, sortOrder]);

  // Memoize visible chunks slice
  const visibleChunks = useMemo(
    () => sortedChunks.slice(0, visibleCount),
    [sortedChunks, visibleCount]
  );

  const hasMore = visibleCount < sortedChunks.length;

  // Memoize callbacks to prevent re-creating functions on every render
  const handleChunkClick = useCallback((chunk: GuidelineChunk) => {
    setSelectedChunk(chunk);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedChunk(null);
  }, []);

  const handleToggleEmbed = useCallback(async (chunk: GuidelineChunk, toEmbed: boolean) => {
    // Directly request embed/skip without additional confirmation
    setLoadingChunkIds((prev) => new Set(prev).add(chunk.chunk_id));
    try {
      const response = await guidelinesApi.updateChunk(String(guidelineId), String(chunk.chunk_id), { to_embed: toEmbed });

      // Update local state with the response data
      setLocalChunks((prevChunks) =>
        prevChunks.map((c) =>
          c.chunk_id === chunk.chunk_id ? { ...c, ...response.data } : c
        )
      );

      const message = toEmbed
        ? `Chunk ${chunk.chunk_index} marked for embedding`
        : chunk.embed_status
          ? `Chunk ${chunk.chunk_index} skipped and embeddings deleted`
          : `Chunk ${chunk.chunk_index} marked as skipped`;
      setSuccessMessage(message);
    } catch (err) {
      console.error(toEmbed ? "Failed to mark chunk to embed:" : "Failed to skip chunk:", err);
      alert("Failed to update chunk. See console for details.");
    } finally {
      setLoadingChunkIds((prev) => {
        const next = new Set(prev);
        next.delete(chunk.chunk_id);
        return next;
      });
    }
  }, [guidelineId]);

  // Confirmation dialog removed: skipping is immediate via handleToggleEmbed


  // Clicking a column header: toggle direction if already active, else sort asc
  const handleColumnSort = useCallback((field: SortField) => {
    setSortField(field);
    setSortOrder((prev) => (sortField === field && prev === 'asc' ? 'desc' : 'asc'));
  }, [sortField]);

  const loadMore = useCallback(() => {
    if (hasMore) {
      setVisibleCount((prev) => Math.min(prev + CHUNKS_PER_PAGE, sortedChunks.length));
    }
  }, [hasMore, sortedChunks.length]);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [loadMore, hasMore]);

  // Poll for pending chunks every 10 seconds
  // Use a ref to track current chunks to avoid stale closure issues
  const localChunksRef = useRef<GuidelineChunk[]>(localChunks);
  useEffect(() => {
    localChunksRef.current = localChunks;
  }, [localChunks]);

  useEffect(() => {
    // Start polling unconditionally - the interval will check for pending chunks
    const pollInterval = setInterval(async () => {
      const currentChunks = localChunksRef.current;
      // Check if there are any pending chunks (to_embed=true but no embed_status)
      const hasPendingChunks = currentChunks.some((chunk) => chunk.to_embed && !chunk.embed_status);

      if (!hasPendingChunks) {
        return; // No pending chunks to poll for
      }

      console.log('Polling for chunk embedding updates...');
      try {
        // First, trigger backend to sync any completed embeddings from inference server
        const syncResult = await guidelinesApi.syncPendingEmbeddings();
        if (syncResult.data.synced > 0) {
          console.log(`Backend synced ${syncResult.data.synced} embeddings from inference server`);
        }

        // Then fetch updated guideline data
        const response = await guidelinesApi.get(String(guidelineId));
        const updatedChunks = response.data.chunks;

        // Update local chunks with new data
        setLocalChunks((prevChunks) => {
          const updatedMap = new Map(updatedChunks.map((c) => [c.chunk_id, c]));
          return prevChunks.map((chunk) => updatedMap.get(chunk.chunk_id) || chunk);
        });

        // Check if any chunks transitioned to embedded
        const newlyEmbedded = updatedChunks.filter((newChunk) => {
          const oldChunk = currentChunks.find((c) => c.chunk_id === newChunk.chunk_id);
          return oldChunk?.to_embed && !oldChunk.embed_status && newChunk.embed_status;
        });

        if (newlyEmbedded.length > 0) {
          console.log(`Detected ${newlyEmbedded.length} newly embedded chunks`);
          setSuccessMessage(
            `${newlyEmbedded.length} chunk${newlyEmbedded.length > 1 ? 's' : ''} successfully embedded`
          );
        }
      } catch (err) {
        console.error("Failed to poll chunk updates:", err);
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, [guidelineId]); // Only depend on guidelineId, not localChunks

  // Early return after all hooks are defined
  if (localChunks.length === 0) return null;

  return (
    <S.StyledChunksPaper elevation={1}>
      <Box sx={S.StyledChunkHeader}>
        <Typography variant="subtitle1" fontWeight={600}>
          Chunks
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click a column header to sort
        </Typography>
      </Box>

      {/* Column header row — grid matches ChunkItem */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '10% 10% 60% 10% 10%',
        gap: 1,
        px: 2,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
      }}>
        {(['chunk_index', 'page_no'] as SortField[]).map((field) => (
          <Box
            key={field}
            onClick={() => handleColumnSort(field)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', userSelect: 'none',
              color: sortField === field ? 'text.primary' : 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <Typography variant="caption" fontWeight={600} color="inherit">
              {field === 'chunk_index' ? 'Chunk #' : 'Page'}
            </Typography>
            {sortField === field
              ? (sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 12 }} /> : <ArrowDownwardIcon sx={{ fontSize: 12 }} />)
              : null}
          </Box>
        ))}
        <Typography variant="caption" color="text.secondary" fontWeight={600}>Text</Typography>
        <Box
          onClick={() => handleColumnSort('status')}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, cursor: 'pointer', userSelect: 'none',
            color: sortField === 'status' ? 'text.primary' : 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <Typography variant="caption" fontWeight={600} color="inherit">Status</Typography>
          {sortField === 'status'
            ? (sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 12 }} /> : <ArrowDownwardIcon sx={{ fontSize: 12 }} />)
            : null}
        </Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textAlign: 'center' }}>Action</Typography>
      </Box>

      <Box sx={S.StyledChunksContainer}>
        {visibleChunks.map((chunk) => (
          <ChunkItemMemo
            key={chunk.chunk_id}
            chunk={chunk}
            onChunkClick={handleChunkClick}
            onToggleEmbed={handleToggleEmbed}
            isLoading={loadingChunkIds.has(chunk.chunk_id)}
          />
        ))}
        {/* Load more trigger and indicator */}
        {hasMore && (
          <Box
            ref={loadMoreRef}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Loading more chunks...
            </Typography>
          </Box>
        )}
        {!hasMore && visibleChunks.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              All {sortedChunks.length} chunks loaded
            </Typography>
          </Box>
        )}
      </Box>

      {/* Chunk Details Modal - only render content when open */}
      <Dialog
        open={selectedChunk !== null}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        {selectedChunk && (
          <ChunkDetailContent
            chunk={selectedChunk}
            actionLoading={actionLoading}
            onClose={handleCloseModal}
            onToggleEmbed={handleToggleEmbed}
          />
        )}
      </Dialog>

      {/* Confirmation dialog removed: skipping is immediate */}

      {/* Success notification */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
    </S.StyledChunksPaper>
  );
};

// Memoized chunk item component to prevent unnecessary re-renders
interface ChunkItemProps {
  chunk: GuidelineChunk;
  onChunkClick: (chunk: GuidelineChunk) => void;
  onToggleEmbed: (chunk: GuidelineChunk, toEmbed: boolean) => void;
  isLoading: boolean;
}

const ChunkItem: React.FC<ChunkItemProps> = ({ chunk, onChunkClick, onToggleEmbed, isLoading }) => {
  const handleClick = useCallback(() => {
    onChunkClick(chunk);
  }, [chunk, onChunkClick]);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the modal
    if (chunk.embed_status || chunk.to_embed) {
      // If embedded or pending, skip it
      onToggleEmbed(chunk, false);
    } else {
      // If skipped, embed it
      onToggleEmbed(chunk, true);
    }
  }, [chunk, onToggleEmbed]);

  const chipLabel = chunk.embed_status
    ? "Embedded"
    : chunk.to_embed
      ? "Pending"
      : "Skip";

  const chipBgColor = chunk.embed_status
    ? ColorsEnum.green
    : chunk.to_embed
      ? ColorsEnum.sunshine
      : ColorsEnum.grey;

  const chipColor = chunk.embed_status
    ? ColorsEnum.white
    : ColorsEnum.gunmetal;

  const buttonLabel = chunk.embed_status || chunk.to_embed ? "Skip" : "Embed";
  const buttonColor = chunk.embed_status || chunk.to_embed ? "error" : "primary";

  return (
    <Box
      sx={{ ...S.StyledChunkItem, display: 'grid', gridTemplateColumns: '10% 10% 60% 10% 10%', gap: 1, alignItems: 'start' }}
      onClick={handleClick}
    >
      {/* Chunk # */}
      <Typography variant="body2" fontWeight={600} sx={{ pt: 0.25 }}>
        {chunk.chunk_index}
      </Typography>

      {/* Page */}
      <Typography variant="body2" fontWeight={600} sx={{ pt: 0.25 }}>
        {chunk.page_no}
      </Typography>

      {/* Text preview */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontSize: "0.85rem", minWidth: 0 }}
      >
        {chunk.text?.slice(0, 200)}
        {(chunk.text?.length ?? 0) > 200 ? "…" : ""}
      </Typography>

      {/* Status chip */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Chip
          label={chipLabel}
          size="small"
          sx={{
            bgcolor: chipBgColor,
            color: chipColor,
            fontWeight: 600,
            borderRadius: "12px",
            fontSize: "0.7rem",
          }}
        />
      </Box>

      {/* Action button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          color={buttonColor}
          onClick={handleToggle}
          disabled={isLoading}
          sx={{
            fontSize: "0.7rem",
            py: 0.25,
            px: 1,
            minWidth: 0,
            textTransform: "none",
          }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CircularProgress size={14} />
              <span>Loading</span>
            </Box>
          ) : (
            buttonLabel
          )}
        </Button>
      </Box>
    </Box>
  );
};

const ChunkItemMemo = React.memo(ChunkItem);

// Memoized chunk detail content to avoid re-rendering when dialog is closed
interface ChunkDetailContentProps {
  chunk: GuidelineChunk;
  actionLoading: boolean;
  onClose: () => void;
  onToggleEmbed: (chunk: GuidelineChunk, toEmbed: boolean) => void;
}

const ChunkDetailContent: React.FC<ChunkDetailContentProps> = ({
  chunk,
  actionLoading,
  onClose,
  onToggleEmbed,
}) => {
  const handleEmbed = useCallback(() => {
    onToggleEmbed(chunk, true);
  }, [chunk, onToggleEmbed]);

  const handleSkip = useCallback(() => {
    onToggleEmbed(chunk, false);
  }, [chunk, onToggleEmbed]);

  const chipLabel = chunk.embed_status
    ? "Embedded"
    : chunk.to_embed
      ? "Pending"
      : "Skip";

  const chipBgColor = chunk.embed_status
    ? ColorsEnum.green
    : chunk.to_embed
      ? ColorsEnum.sunshine
      : ColorsEnum.grey;

  const chipColor = chunk.embed_status
    ? ColorsEnum.white
    : ColorsEnum.gunmetal;

  return (
    <>
      <DialogTitle>
        Chunk {chunk.chunk_index} (Page {chunk.page_no})
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Status
            </Typography>
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                bgcolor: chipBgColor,
                color: chipColor,
                fontWeight: 600,
              }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Guideline Chunk ID
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {chunk.chunk_id}
              </Typography>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={() => navigator.clipboard.writeText(String(chunk.chunk_id))}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Chunk Index
            </Typography>
            <Typography variant="body2">{chunk.chunk_index}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Order Index
            </Typography>
            <Typography variant="body2">{chunk.order_index}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Page Number
            </Typography>
            <Typography variant="body2">{chunk.page_no}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Full Text
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                p: 2,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                borderRadius: 1,
                maxHeight: 400,
                overflowY: "auto",
              }}
            >
              {chunk.text}
            </Typography>
          </Box>

          {chunk.embed_status && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Embedding Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Model: {chunk.embed_status.embedding_model}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Created: {new Date(chunk.embed_status.created_at).toLocaleString()}
              </Typography>
              {chunk.embed_status.task_id && (
                <Typography variant="body2" color="text.secondary">
                  Task ID: {chunk.embed_status.task_id}
                </Typography>
              )}
            </Box>
          )}

          {chunk.task_id && !chunk.embed_status && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Pending Task
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Task ID: {chunk.task_id}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={actionLoading} variant="ghost">Close</Button>
        {chunk.embed_status ? (
          // Already embedded: allow skip (delete embeddings)
          <Button
            variant="contained"
            color="error"
            onClick={handleSkip}
            disabled={actionLoading}
          >
            Skip
          </Button>
        ) : chunk.to_embed ? (
          // Pending: allow skip
          <Button
            variant="contained"
            color="error"
            onClick={handleSkip}
            disabled={actionLoading}
          >
            Skip
          </Button>
        ) : (
          // Skipped: allow re-embed
          <Button
            variant="contained"
            color="primary"
            onClick={handleEmbed}
            disabled={actionLoading}
          >
            Embed
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default ChunksList;
